// reducer.js

import * as types from "./_actionTypes";

const initialState = {
  availableCourses: [],
  enrolledCourses: [],
  loading: false,
  enrolling: false,
  error: null,
  successMessage: null,
  loadingEnrolledCourses: false,
  enrolledCoursesError: null,
  contents: [],
  stats: null,
  currentContent: null,
  nextContent: null,
  statsLoading: false,
  progressLoading: false,
};

const studentCoursesReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch Enrolled Courses
    case types.FETCH_ENROLLED_COURSES_REQUEST:
      return {
        ...state,
        loadingEnrolledCourses: true,
        enrolledCoursesError: null,
      };
    case types.FETCH_ENROLLED_COURSES_SUCCESS:
      return {
        ...state,
        enrolledCourses: action.payload || [],
        loadingEnrolledCourses: false,
        enrolledCoursesError: null,
      };
    case types.FETCH_ENROLLED_COURSES_FAILURE:
      return {
        ...state,
        loadingEnrolledCourses: false,
        enrolledCoursesError: action.payload,
        enrolledCourses: [],
      };
    case types.FETCH_AVAILABLE_COURSES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case types.FETCH_AVAILABLE_COURSES_SUCCESS:
      return {
        ...state,
        availableCourses: action.payload.availableCourses || [],
        loading: false,
        error: null,
      };

    case types.FETCH_AVAILABLE_COURSES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        availableCourses: [],
        enrolledCourses: [],
      };

    // Enroll Course
    case types.ENROLL_COURSE_REQUEST:
      return {
        ...state,
        enrolling: true,
        error: null,
        successMessage: null,
      };

    case types.ENROLL_COURSE_SUCCESS:
      return {
        ...state,
        enrolling: false,
        successMessage: "Successfully enrolled in course!",
        error: null,
      };

    case types.ENROLL_COURSE_FAILURE:
      return {
        ...state,
        enrolling: false,
        error: action.payload,
      };

    // Unenroll Course
    case types.UNENROLL_COURSE_REQUEST:
      return {
        ...state,
        enrolling: true,
        error: null,
        successMessage: null,
      };

    case types.UNENROLL_COURSE_SUCCESS:
      return {
        ...state,
        enrolling: false,
        successMessage: "Successfully unenrolled from course!",
        error: null,
      };

    case types.UNENROLL_COURSE_FAILURE:
      return {
        ...state,
        enrolling: false,
        error: action.payload,
      };

    // Clear Message
    case types.CLEAR_ENROLLMENT_MESSAGE:
      return {
        ...state,
        successMessage: null,
        error: null,
      };

    // Clear Course Content - resets all content-related state
    case types.CLEAR_COURSE_CONTENT:
      return {
        ...state,
        contents: [],
        currentContent: null,
        stats: null,
        error: null,
      };

    case types.UPDATE_VIDEO_PROGRESS:
      return {
        ...state,
        videoProgress: {
          ...state.videoProgress,
          [action.payload.videoId]: action.payload.progress,
        },
      };

    case types.COMPLETE_VIDEO:
      return {
        ...state,
        completedVideos: [...(state.completedVideos || []), action.payload],
      };

    // Student Fetch Course Contents
    case types.FETCH_STUDENT_COURSE_CONTENTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case types.FETCH_STUDENT_COURSE_CONTENTS_SUCCESS:
      return {
        ...state,
        loading: false,
        contents: action.payload.contents,
        error: null,
      };
    case types.FETCH_STUDENT_COURSE_CONTENTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };

    // Student Fetch Content Stats
    case types.FETCH_STUDENT_CONTENT_STATS_REQUEST:
      return {
        ...state,
        statsLoading: true,
        error: null,
      };
    case types.FETCH_STUDENT_CONTENT_STATS_SUCCESS:
      return {
        ...state,
        statsLoading: false,
        stats: action.payload.stats,
        error: null,
      };
    case types.FETCH_STUDENT_CONTENT_STATS_FAILURE:
      return {
        ...state,
        statsLoading: false,
        error: action.payload.error,
      };

    // Student Fetch Content By ID
    case types.FETCH_STUDENT_CONTENT_BY_ID_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case types.FETCH_STUDENT_CONTENT_BY_ID_SUCCESS:
      return {
        ...state,
        loading: false,
        currentContent: action.payload.content,
        error: null,
      };
    case types.FETCH_STUDENT_CONTENT_BY_ID_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };

    // Student Update Progress
    case types.UPDATE_STUDENT_CONTENT_PROGRESS_REQUEST:
      return {
        ...state,
        progressLoading: true,
        error: null,
      };
    case types.UPDATE_STUDENT_CONTENT_PROGRESS_SUCCESS:
      return {
        ...state,
        progressLoading: false,
        contents: state.contents.map((content) =>
          content.id === action.payload.contentId
            ? { ...content, progress: action.payload.progress }
            : content
        ),
        currentContent:
          state.currentContent?.id === action.payload.contentId
            ? { ...state.currentContent, progress: action.payload.progress }
            : state.currentContent,
        successMessage: "Progress updated successfully",
        error: null,
      };
    case types.UPDATE_STUDENT_CONTENT_PROGRESS_FAILURE:
      return {
        ...state,
        progressLoading: false,
        error: action.payload.error,
      };

    // Student Mark Complete
    case types.MARK_STUDENT_CONTENT_COMPLETE_REQUEST:
      return {
        ...state,
        progressLoading: true,
        error: null,
      };
    case types.MARK_STUDENT_CONTENT_COMPLETE_SUCCESS:
      return {
        ...state,
        progressLoading: false,
        contents: state.contents.map((content) =>
          content.id === action.payload.contentId
            ? {
                ...content,
                progress: {
                  ...content.progress,
                  is_completed: true,
                  completion_percentage: 100,
                },
              }
            : content
        ),
        currentContent:
          state.currentContent?.id === action.payload.contentId
            ? {
                ...state.currentContent,
                progress: {
                  ...state.currentContent.progress,
                  is_completed: true,
                  completion_percentage: 100,
                },
              }
            : state.currentContent,
        successMessage: "Content marked as complete",
        error: null,
      };
    case types.MARK_STUDENT_CONTENT_COMPLETE_FAILURE:
      return {
        ...state,
        progressLoading: false,
        error: action.payload.error,
      };

    // Student Fetch Next Content
    case types.FETCH_STUDENT_NEXT_CONTENT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case types.FETCH_STUDENT_NEXT_CONTENT_SUCCESS:
      return {
        ...state,
        loading: false,
        nextContent: action.payload.content,
        error: null,
      };
    case types.FETCH_STUDENT_NEXT_CONTENT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };

    default:
      return state;
  }
};

export default studentCoursesReducer;